/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  Database,
  ClipboardCheck,
  BookOpen,
  Folder,
  Users,
  Activity,
  Save,
  Trash2,
  LogOut,
  RefreshCw,
  Download,
  Plus,
  Star,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { adminApi, clearAdminSession, getAdminExp, getAdminToken, secondsUntilAdminExpiry, setAdminSession, ADMIN_SESSION_WARN_THRESHOLD_SECONDS } from "@/lib/adminApi";
import { BACKEND_URL } from "@/lib/api";
import AdminDatabaseTab from "@/components/admin/AdminDatabaseTab";
import AdminJurisdictionsTab from "@/components/admin/AdminJurisdictionsTab";
import AdminKnowledgeBaseTab from "@/components/admin/AdminKnowledgeBaseTab";
import AdminDocumentLibraryTab from "@/components/admin/AdminDocumentLibraryTab";
import AdminUsersTab from "@/components/admin/AdminUsersTab";
import AdminMonitorTab from "@/components/admin/AdminMonitorTab";
import AdminBlogTab from "@/components/admin/AdminBlogTab";
import { RegulensMark } from "@/components/RegulensMark";

const TABS = [
  { key: "database", label: "Database Management", Icon: Database },
  { key: "status", label: "Jurisdiction Status", Icon: ClipboardCheck },
  { key: "kb", label: "Knowledge Base", Icon: BookOpen },
  { key: "docs", label: "Document Library", Icon: Folder },
  { key: "users", label: "User Management", Icon: Users },
  { key: "monitor", label: "System Monitor", Icon: Activity },
  { key: "blog", label: "Blog Manager", Icon: BookOpen },
];

function HeaderSessionInfo({ onExtend, onSignOut }) {
  const [secs, setSecs] = useState(secondsUntilAdminExpiry());
  useEffect(() => {
    const id = setInterval(() => setSecs(secondsUntilAdminExpiry()), 1000);
    return () => clearInterval(id);
  }, []);
  if (secs == null || secs <= 0) return null;
  const mins = Math.floor(secs / 60);
  const remSec = secs % 60;
  const warn = secs <= ADMIN_SESSION_WARN_THRESHOLD_SECONDS;
  return (
    <div className="flex items-center gap-2" data-testid="admin-header-session">
      <span
        className={`text-[11px] font-mono ${warn ? "text-[var(--rg-amber)] font-bold" : "text-[var(--rg-text)]"}`}
        data-testid="admin-header-countdown"
        title="Admin session expires in — click Extend to reset to 60 minutes."
      >
        {mins}:{String(remSec).padStart(2, "0")}
      </span>
      <Button
        size="sm"
        onClick={onExtend}
        className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)] font-semibold text-[11px] h-7 px-2"
        data-testid="admin-header-extend"
      >
        Extend
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={onSignOut}
        className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)] text-[11px] h-7 px-2"
        data-testid="admin-header-signout"
      >
        <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out
      </Button>
    </div>
  );
}

function SessionBanner({ onExtend, onSignOut }) {
  const [secs, setSecs] = useState(secondsUntilAdminExpiry());
  useEffect(() => {
    const id = setInterval(() => setSecs(secondsUntilAdminExpiry()), 1000);
    return () => clearInterval(id);
  }, []);
  if (secs == null || secs <= 0) return null;
  // Item 1 — banner appears at 5 minutes remaining (55 minutes elapsed of a
  // 60-minute token), per Constraint 1.
  if (secs > ADMIN_SESSION_WARN_THRESHOLD_SECONDS) return null;
  const mins = Math.floor(secs / 60);
  const remSec = secs % 60;
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-md px-4 py-2 mb-4 border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)]"
      data-testid="admin-session-warning-banner"
      role="alert"
    >
      <div className="text-sm text-[var(--rg-text-strong)]">
        Your session expires in{" "}
        <span className="font-mono font-bold text-[var(--rg-amber)]" data-testid="admin-session-countdown">
          {mins}m {String(remSec).padStart(2, "0")}s
        </span>
        . Click <span className="font-bold">Extend</span> to stay logged in.
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onExtend} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold text-xs h-7" data-testid="admin-extend-session">
          Extend
        </Button>
        <Button size="sm" variant="outline" onClick={onSignOut} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)] text-xs h-7" data-testid="admin-signout">
          <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("database");

  useEffect(() => {
    if (!getAdminToken()) navigate("/admin/login");
  }, [navigate]);

  // Auto-redirect on expiry
  useEffect(() => {
    const id = setInterval(() => {
      const secs = secondsUntilAdminExpiry();
      if (secs == null || secs <= 0) {
        clearAdminSession();
        toast.error("Admin session expired. Please sign in again.");
        navigate("/admin/login");
      }
    }, 5000);
    return () => clearInterval(id);
  }, [navigate]);

  const extend = async () => {
    try {
      const { data } = await adminApi.post("/admin/auth/extend");
      setAdminSession(data.admin_token, data.expires_at);
      toast.success("Session extended — 60 minutes.");
    } catch {
      toast.error("Could not extend session");
    }
  };

  const signOut = async () => {
    try { await adminApi.post("/admin/auth/logout"); } catch {}
    clearAdminSession();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[var(--rg-bg)] text-[var(--rg-text-strong)] flex flex-col">
      <Toaster theme="dark" position="top-right" offset={80} />

      {/* Header */}
      <header className="border-b border-[var(--rg-border)] bg-[var(--rg-bg)]/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RegulensMark size={22} theme="dark" />
            <span className="font-heading font-extrabold text-[var(--rg-text-strong)]" data-testid="admin-header">Regulens Admin Console</span>
            <span className="ml-2 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider text-[var(--rg-red)] border border-[var(--rg-red)] bg-[var(--rg-red-soft)]">RESTRICTED</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]"
              data-testid="admin-back-to-app"
            >
              ← Back to app
            </a>
            <HeaderSessionInfo onExtend={extend} onSignOut={signOut} />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-6">
        <SessionBanner onExtend={extend} onSignOut={signOut} />

        {/* Tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-7 gap-2 mb-6" data-testid="admin-tabs">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex items-center justify-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-[0.16em] font-bold rounded-md py-2 border transition-fast ${
                tab === key
                  ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] border-[var(--rg-sage)]"
                  : "bg-[var(--rg-surface)] text-[var(--rg-text)] border-[var(--rg-border)] hover:border-[var(--rg-border-hi)]"
              }`}
              data-testid={`admin-tab-${key}`}
            >
              <Icon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {tab === "database" && <AdminDatabaseTab />}
        {tab === "status" && <AdminJurisdictionsTab />}
        {tab === "kb" && <AdminKnowledgeBaseTab />}
        {tab === "docs" && <AdminDocumentLibraryTab />}
        {tab === "users" && <AdminUsersTab />}
        {tab === "monitor" && <AdminMonitorTab />}
        {tab === "blog" && <AdminBlogTab />}
      </main>

      <footer
        className="border-t border-[var(--rg-border)] px-6 py-3 text-[10px] text-[var(--rg-muted)] font-mono flex flex-wrap items-center gap-x-4 gap-y-1 justify-center"
        data-testid="admin-footer"
      >
        <span>© 2026 Regulens · Admin Console</span>
        <a href="/privacy-policy" target="_blank" rel="noreferrer" className="hover:text-[var(--rg-text-strong)]" data-testid="admin-footer-privacy">Privacy Policy</a>
        <a href="/terms-of-service" target="_blank" rel="noreferrer" className="hover:text-[var(--rg-text-strong)]" data-testid="admin-footer-terms">Terms of Service</a>
        <a href="/refund-policy" target="_blank" rel="noreferrer" className="hover:text-[var(--rg-text-strong)]" data-testid="admin-footer-refund">Refund Policy</a>
      </footer>
    </div>
  );
}
